
import { useState, useEffect } from 'react';
import { enhancedImageCacheService } from '@/services/offline/image-cache-enhanced.service';
import OfflineLogger from '@/utils/offlineLogger';
import { networkService } from '@/services/network.service';

interface ProductImageProps {
  productId: string;
  photo?: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
}

export default function ProductImage({ 
  productId, 
  photo, 
  alt, 
  className = "", 
  fallbackSrc = "/placeholder.svg" 
}: ProductImageProps) {
  const [imageSrc, setImageSrc] = useState<string>(fallbackSrc);
  const [isLoading, setIsLoading] = useState(!!photo);
  const [isOnline, setIsOnline] = useState(networkService.isOnline());

  useEffect(() => {
    const unsubscribe = networkService.addListener((status) => {
      setIsOnline(status.connected);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!photo) {
      setImageSrc(fallbackSrc);
      setIsLoading(false);
      return;
    }

    const loadImage = async () => {
      try {
        setIsLoading(true);
        const cachedUrl = await enhancedImageCacheService.getImageUrl(productId, photo);
        if (cachedUrl) {
          setImageSrc(cachedUrl);
        } else {
          setImageSrc(fallbackSrc);
        }
      } catch (error) {
        OfflineLogger.error('ProductImage', `Error loading image: ${error}`);
        setImageSrc(fallbackSrc);
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();
  }, [productId, photo, fallbackSrc]);

  if (isLoading) {
    return (
      <div className={`bg-gray-200 animate-pulse flex items-center justify-center ${className}`}>
        <span className="text-gray-400 text-xs">
          {isOnline ? "Caricamento..." : "Offline"}
        </span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onError={() => setImageSrc(fallbackSrc)}
    />
  );
}
